/** @jsxImportSource theme-ui */
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { ThemeUIStyleObject } from 'theme-ui';

import theme from 'theme';

import { AdminNavUrls, FeatureNavUrls, SettingNavUrls } from '../NavLinks';
import NavDrawerExpandMenu from './NavDrawerExpandMenu';
import NavDrawerItem from './NavDrawerItem';

const Styles: ThemeUIStyleObject = {
  display: ['flex', null, 'none'], // Always not around on higher values
  width: 0,
  '& .overlay': {
    width: 0,
    left: 0,
    transition: 'background ease-in 0.3s',
    position: 'absolute',
    cursor: 'pointer',
  },
  '& .menu-content': {
    width: 0,
    right: 0,
    position: 'absolute',
    transition: 'width ease-in 0.3s',
    display: 'flex',
    flexDirection: 'column',
    '& .settings-submenu': {
      my: '16px',
      display: 'flex',
      flexDirection: 'column',
    },
    '& .top-level-nav-item': { display: 'none' }, // So tab target can't affect these when closed
    '& .top-level-nav-item:not(.compact)': { borderBottom: `1px solid ${theme.colors.deepSea50}` },
    '& .drawer-item:hover, .drawer-item:focus': {
      background: theme.colors.deepSea80,
      color: theme.colors.deepSea40,
      outline: 'none',
    },
  },
  '&.is-open': {
    background: 'transparent',
    position: 'absolute',
    top: 44,
    right: 0,
    height: 'calc(100vh - 44px)',
    width: '100vw',
    '& .top-level-nav-item': { display: 'block' },
    '& .overlay': {
      width: [0, '100%'],
      height: '100%',
      background: 'rgba(60, 63, 77, 0.5)',
    },
    '& .menu-content': {
      width: ['100%', '50%'],
      height: '100%',
      maxHeight: '100%',
      overflowY: 'auto',
      pb: '32px',
      backgroundColor: theme.colors.deepSea90,
    },
  },
};

type NavigationDrawerProps = {
  isOpen: boolean;
  adminUser: boolean;
  handleCancel: () => void;
  onItemClick: (link: string) => void;
};

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  isOpen,
  adminUser,
  handleCancel,
  onItemClick,
}) => {
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCancel();
    };

    if (isOpen) window.addEventListener('keypress', handleKeyPress, false);

    return () => window.removeEventListener('keypress', handleKeyPress, false);
  }, [isOpen, handleCancel]);

  return (
    <div className={classNames({ 'nav-drawer': true, 'is-open': isOpen })} sx={Styles}>
      <div className="overlay" onClick={handleCancel} />
      <div className="menu-content">
        {FeatureNavUrls.map(navItem => (
          <React.Fragment key={navItem.title}>
            {navItem.children?.length ? (
              <NavDrawerExpandMenu navItem={navItem} onItemClick={onItemClick} />
            ) : (
              <NavDrawerItem navItem={navItem} onClick={onItemClick} />
            )}
          </React.Fragment>
        ))}
        {adminUser && <NavDrawerExpandMenu navItem={AdminNavUrls[0]} onItemClick={onItemClick} />}
        <div className="settings-submenu">
          {SettingNavUrls.map(settings =>
            settings.children.map(navItem => (
              <NavDrawerItem key={navItem.title} navItem={navItem} small onClick={onItemClick} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NavigationDrawer;
